import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';

/** Matches untimed semantics used across flow UI and {@link computeAllSlotTimings}. */
export function isUntimedSlot(slot: FlowSlotDto): boolean {
  return Boolean(
    slot.isUntimed ?? (slot.plannedOffsetMinutes === undefined && slot.computedOffsetMinutes === undefined),
  );
}

/**
 * Pure timing utilities for flow slots.
 * These helpers are intentionally free of React state to keep them easy to test.
 */
export function calculateFlowDurationFromSlots(slots: FlowSlotDto[]): number | undefined {
  if (!slots || slots.length === 0) {
    return undefined;
  }

  const timedSlots = slots.filter(
    (slot) => slot.plannedOffsetMinutes !== undefined || slot.computedOffsetMinutes !== undefined,
  );

  if (timedSlots.length === 0) {
    return undefined;
  }

  const computedSlots = computeAllSlotTimings(timedSlots, undefined);

  let lastFixedSlotIndex: number | undefined;
  for (let i = computedSlots.length - 1; i >= 0; i--) {
    if (computedSlots[i].plannedOffsetMinutes !== undefined) {
      lastFixedSlotIndex = i;
      break;
    }
  }

  if (lastFixedSlotIndex !== undefined) {
    const lastFixedSlot = computedSlots[lastFixedSlotIndex];
    const startOffset = lastFixedSlot.plannedOffsetMinutes ?? 0;

    let totalDuration = 0;
    for (let i = lastFixedSlotIndex; i < computedSlots.length; i++) {
      const slot = computedSlots[i];
      const plannedDuration =
        slot.plannedDurationMinutes ?? slot.content?.plannedDurationMinutes ?? slot.computedDurationMinutes ?? 0;
      totalDuration += plannedDuration;
    }

    return startOffset + totalDuration;
  }

  let totalDuration = 0;
  for (const slot of computedSlots) {
    const plannedDuration =
      slot.plannedDurationMinutes ?? slot.content?.plannedDurationMinutes ?? slot.computedDurationMinutes ?? 0;
    totalDuration += plannedDuration;
  }

  return totalDuration;
}

export function computeAllSlotTimings(slots: FlowSlotDto[], flowDuration?: number): FlowSlotDto[] {
  if (!Array.isArray(slots)) {
    return [];
  }
  const processedSlots = slots.map((slot) => ({ ...slot }));

  processedSlots.forEach((slot, index) => {
    if (isUntimedSlot(slot)) {
      return;
    }

    if (slot.plannedOffsetMinutes !== undefined) {
      slot.computedOffsetMinutes = slot.plannedOffsetMinutes;
    } else if (index > 0) {
      const previousSlot = processedSlots[index - 1];
      const previousEnd = (previousSlot.computedOffsetMinutes ?? 0) + (previousSlot.computedDurationMinutes ?? 0);
      slot.computedOffsetMinutes = previousEnd;
    } else {
      slot.computedOffsetMinutes = 0;
    }

    const plannedDuration =
      slot.plannedDurationMinutes ?? slot.content?.plannedDurationMinutes ?? slot.computedDurationMinutes ?? 0;

    let computedDuration = plannedDuration;
    const slotStart = slot.computedOffsetMinutes ?? 0;

    if (flowDuration !== undefined && flowDuration > 0) {
      const slotEnd = slotStart + plannedDuration;
      if (slotEnd > flowDuration) {
        const availableSpace = flowDuration - slotStart;
        computedDuration = Math.max(0, availableSpace);
      }
    }

    for (let i = index + 1; i < processedSlots.length; i++) {
      const nextSlot = processedSlots[i];
      if (nextSlot.plannedOffsetMinutes !== undefined) {
        const nextFixedSlotStart = nextSlot.plannedOffsetMinutes;
        const availableTime = nextFixedSlotStart - slotStart;
        if (plannedDuration > availableTime) {
          computedDuration = Math.max(0, availableTime);
        }
        break;
      }
    }

    slot.computedDurationMinutes = computedDuration;
  });

  return processedSlots;
}

export function isTimedSlot(slot: FlowSlotDto): boolean {
  return slot.plannedOffsetMinutes !== undefined || slot.computedOffsetMinutes !== undefined;
}

export function getEffectivePlannedDuration(slot: FlowSlotDto, overrideMinutes?: number): number {
  if (overrideMinutes !== undefined) return overrideMinutes;
  return slot.plannedDurationMinutes ?? slot.content?.plannedDurationMinutes ?? slot.computedDurationMinutes ?? 0;
}

export type SlotExpandShortenMetrics = {
  /** Remaining planned minutes before the next fixed boundary (negative = overlap). */
  gap: number;
  /** Planned minus computed duration on the edited slot. */
  shortenedBy: number;
  /** Whether expand/shorten actions apply (fixed end-time or later fixed start). */
  hasFixedBoundary: boolean;
};

/**
 * Computes expand/shorten metrics for a slot within a timed sequence.
 * Uses the same boundary rules as {@link computeAllSlotTimings}.
 */
export function computeSlotExpandShortenMetrics(
  timedSlots: FlowSlotDto[],
  slotId: string,
  options?: { flowDuration?: number; editingPlannedDuration?: number },
): SlotExpandShortenMetrics {
  const flowDuration = options?.flowDuration;
  const editingPlannedDuration = options?.editingPlannedDuration;

  const slotsWithEdit = timedSlots.map((s) => {
    if (s.id !== slotId) return s;
    const plannedDuration = editingPlannedDuration ?? getEffectivePlannedDuration(s);
    return { ...s, plannedDurationMinutes: plannedDuration };
  });

  const computed = computeAllSlotTimings(slotsWithEdit, flowDuration);
  const slotIndex = computed.findIndex((s) => s.id === slotId);
  if (slotIndex === -1) {
    return { gap: 0, shortenedBy: 0, hasFixedBoundary: false };
  }

  const slot = computed[slotIndex];
  const plannedDuration = getEffectivePlannedDuration(slot, editingPlannedDuration);
  const computedDuration = slot.computedDurationMinutes ?? 0;
  const shortenedBy = plannedDuration > computedDuration ? plannedDuration - computedDuration : 0;

  let nextFixedIndex: number | null = null;
  for (let i = slotIndex + 1; i < computed.length; i++) {
    if (computed[i].plannedOffsetMinutes !== undefined) {
      nextFixedIndex = i;
      break;
    }
  }

  const hasFixedEnd = flowDuration !== undefined && flowDuration > 0;
  const hasFixedBoundary = hasFixedEnd || nextFixedIndex !== null;
  if (!hasFixedBoundary) {
    return { gap: 0, shortenedBy, hasFixedBoundary: false };
  }

  const currentStart = slot.plannedOffsetMinutes ?? slot.computedOffsetMinutes ?? 0;
  const windowEnd = nextFixedIndex !== null ? computed[nextFixedIndex].plannedOffsetMinutes! : (flowDuration as number);
  const availableTime = windowEnd - currentStart;

  let sumPlanned = plannedDuration;
  const endIndex = nextFixedIndex ?? computed.length;
  for (let i = slotIndex + 1; i < endIndex; i++) {
    sumPlanned += getEffectivePlannedDuration(computed[i]);
  }

  return {
    gap: availableTime - sumPlanned,
    shortenedBy,
    hasFixedBoundary: true,
  };
}

export type SlotTimingUtils = {
  calculateFlowDurationFromSlots: typeof calculateFlowDurationFromSlots;
  computeAllSlotTimings: typeof computeAllSlotTimings;
  computeSlotExpandShortenMetrics: typeof computeSlotExpandShortenMetrics;
  getEffectivePlannedDuration: typeof getEffectivePlannedDuration;
  isTimedSlot: typeof isTimedSlot;
  isUntimedSlot: typeof isUntimedSlot;
};

export const slotTimingUtils: SlotTimingUtils = {
  calculateFlowDurationFromSlots,
  computeAllSlotTimings,
  computeSlotExpandShortenMetrics,
  getEffectivePlannedDuration,
  isTimedSlot,
  isUntimedSlot,
};

export default computeAllSlotTimings;
